import { tabStateManager } from './tabStateManager';

/**
 * 二进制权限管理工具类
 * 用于处理后台下发的字符串权限并进行权限验证
 */
export class PermissionUserUtil {
  private static instance: PermissionUserUtil;

  // 单例模式
  public static getInstance(): PermissionUserUtil {
    if (!PermissionUserUtil.instance) {
      PermissionUserUtil.instance = new PermissionUserUtil();
    }
    return PermissionUserUtil.instance;
  }

  private constructor() { }

  /**
   * 权限常量定义
   * 从右往左，从0开始计数
   */
  public static readonly PERMISSIONS: Record<string, number> = {
    CREATE_GROUP: 0, // 可创建群 - 最右边第一位
    AUTO_TRANSLATE: 1, // 可自动翻译 - 最右边第二位
    SEARCH_NORMAL_USERS: 2, // 可搜索普通用户 - 最右边第三位
    SWITCH_ACCOUNT: 3, // 可切换账号 - 最右边第四位
    ONLINE_STATUS_VISIBLE: 4, // 可查看在线状态 - 最右边第五位
    BIDIRECTIONAL_DELETE: 5, // 可双向删除 - 最右边第六位
    ANTI_BLOCK: 6, // 可反屏蔽 - 最右边第七位
    ANTI_DELETE: 7, // 可反删除 - 最右边第八位
    MASS_MESSAGE: 8, // 可群发消息 - 最右边第九位
    MASS_MESSAGE_RECEIVE: 9, // 可接收群发消息 - 最右边第十位
    FUTURE_PERMISSION_11: 10, // 可查看未来权限 - 最右边第十一位
    // 可以根据实际需求添加更多权限
  };

  /**
   * 获取租户权限字符串
   * @returns 租户权限字符串，如果不存在返回空字符串
   */
  private getTenantPermissions(): string {
    try {
      const userInfo = tabStateManager.getUserInfo();
      return userInfo?.permissions || '';
    } catch (error) {
      console.warn('获取租户权限失败:', error);
      return '';
    }
  }

  /**
   * 将字符串权限转换为二进制权限值
   * @param permissionString 后台下发的权限字符串，如 "100001000"
   * @returns 对应的权限值，如果转换失败返回0
   */
  public parsePermissionString(permissionString: string): number {
    try {
      // 移除可能的空格和换行符
      const cleanString = permissionString.trim();

      // 验证字符串是否只包含0和1
      if (!/^[01]+$/.test(cleanString)) {
        console.warn('权限字符串格式错误:', permissionString);
        return 0;
      }

      // 将二进制字符串转换为整数
      const permissionValue = parseInt(cleanString, 2);
      console.debug(`权限字符串 ${permissionString} 转换为权限值:`, permissionValue);

      return permissionValue;
    } catch (error) {
      console.error('解析权限字符串失败:', permissionString, '错误:', error);
      return 0;
    }
  }

  /**
   * 检查是否拥有指定权限
   * @param permissionName 要检查的权限名称，如 'CREATE_GROUP'
   * @returns 是否拥有该权限
   */
  public hasPermission(permissionName: string): boolean {
    try {
      const permissionString = this.getTenantPermissions();
      if (!permissionString) {
        return false;
      }

      const permissionValue = this.parsePermissionString(permissionString);
      const permissionBit = PermissionUserUtil.PERMISSIONS[permissionName];

      if (permissionBit === undefined) {
        console.warn('未知的权限名称:', permissionName);
        return false;
      }

      // 使用位运算检查权限
      const hasPermission = (permissionValue & (1 << permissionBit)) !== 0;
      console.debug(`检查权限 ${permissionName} (位${permissionBit}):`, hasPermission);

      return hasPermission;
    } catch (error) {
      console.error('检查权限失败:', error);
      return false;
    }
  }

  /**
   * 检查是否拥有指定权限（通过权限位索引）
   * @param permissionString 后台下发的权限字符串
   * @param permissionBit 权限位索引，从0开始（最右边第一位）
   * @returns 是否拥有该权限
   */
  public hasPermissionByBit(permissionString: string, permissionBit: number): boolean {
    try {
      if (permissionBit < 0) {
        console.warn('权限位索引不能为负数:', permissionBit);
        return false;
      }

      const permissionValue = this.parsePermissionString(permissionString);

      // 使用位运算检查权限
      const hasPermission = (permissionValue & (1 << permissionBit)) !== 0;
      console.debug(`检查权限位 ${permissionBit}:`, hasPermission);

      return hasPermission;
    } catch (error) {
      console.error('检查权限位失败:', error);
      return false;
    }
  }

  /**
   * 获取所有拥有的权限列表
   * @param permissionString 后台下发的权限字符串（可选，如果不传则使用当前用户权限）
   * @returns 拥有的权限名称列表
   */
  public getGrantedPermissions(permissionString?: string): string[] {
    try {
      const permissions = permissionString || this.getTenantPermissions();
      const permissionValue = this.parsePermissionString(permissions);
      const grantedPermissions: string[] = [];

      Object.entries(PermissionUserUtil.PERMISSIONS).forEach(([permissionName, permissionBit]) => {
        if ((permissionValue & (1 << permissionBit)) !== 0) {
          grantedPermissions.push(permissionName);
        }
      });

      console.debug('拥有的权限:', grantedPermissions);
      return grantedPermissions;
    } catch (error) {
      console.error('获取权限列表失败:', error);
      return [];
    }
  }

  /**
   * 获取所有拥有的权限位索引列表
   * @param permissionString 后台下发的权限字符串（可选，如果不传则使用当前用户权限）
   * @returns 拥有的权限位索引列表
   */
  public getGrantedPermissionBits(permissionString?: string): number[] {
    try {
      const permissions = permissionString || this.getTenantPermissions();
      const permissionValue = this.parsePermissionString(permissions);
      const grantedBits: number[] = [];

      // 检查前32位权限（可以根据需要调整）
      for (let i = 0; i < 32; i++) {
        if ((permissionValue & (1 << i)) !== 0) {
          grantedBits.push(i);
        }
      }

      console.debug('拥有的权限位:', grantedBits);
      return grantedBits;
    } catch (error) {
      console.error('获取权限位列表失败:', error);
      return [];
    }
  }

  /**
   * 检查是否拥有多个权限中的任意一个
   * @param permissionNames 要检查的权限名称列表
   * @returns 是否拥有其中任意一个权限
   */
  public hasAnyPermission(permissionNames: string[]): boolean {
    return permissionNames.some(permissionName => this.hasPermission(permissionName));
  }

  /**
   * 检查是否拥有所有指定权限
   * @param permissionNames 要检查的权限名称列表
   * @returns 是否拥有所有权限
   */
  public hasAllPermissions(permissionNames: string[]): boolean {
    return permissionNames.every(permissionName => this.hasPermission(permissionName));
  }

  /**
   * 将权限值转换为二进制字符串
   * @param permissionValue 权限值
   * @param minLength 最小长度，不足时在前面补0
   * @returns 二进制字符串
   */
  public permissionValueToBinaryString(
    permissionValue: number,
    minLength: number = 8
  ): string {
    const binaryString = permissionValue.toString(2);
    return binaryString.padStart(minLength, '0');
  }

  /**
   * 获取权限的详细信息
   * @returns 权限的详细信息
   */
  public getPermissionInfo(): Record<string, any> {
    try {
      const permissionString = this.getTenantPermissions();
      const permissionValue = this.parsePermissionString(permissionString);
      const binaryString = this.permissionValueToBinaryString(permissionValue);
      const grantedPermissions = this.getGrantedPermissions(permissionString);
      const grantedBits = this.getGrantedPermissionBits(permissionString);

      return {
        permissionString,
        permissionValue,
        binaryString,
        grantedPermissions,
        grantedBits,
        totalPermissions: Object.keys(PermissionUserUtil.PERMISSIONS).length,
        grantedCount: grantedPermissions.length,
      };
    } catch (error) {
      console.error('获取权限信息失败:', error);
      return {};
    }
  }

}

// 创建全局单例实例
export const userPermissionUtil = PermissionUserUtil.getInstance();

// 导出权限常量
export const PERMISSIONS = PermissionUserUtil.PERMISSIONS;


/**
 * 便捷的权限检查函数
 */
export const hasPermission = (permissionName: string): boolean => {
  return userPermissionUtil.hasPermission(permissionName);
};

export const hasAnyPermission = (permissionNames: string[]): boolean => {
  return userPermissionUtil.hasAnyPermission(permissionNames);
};

